import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useSupportContext } from '../../../context/SupportContext';
import { useOnboarding } from '../../../context/OnboardingContext';
import { useTheme } from '../../../layouts/SelfServePortalLayout';
import { useI18n } from '../../../context/I18nContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FileText,
  Target,
  Gavel,
  Upload,
  ArrowRight,
  Clock,
  AlertCircle,
  CheckCircle2,
  TrendingUp,
  TrendingDown,
  Sparkles,
  RefreshCw,
  Eye,
  User,
  ChevronRight,
  ChevronLeft,
  Zap,
  Shield,
  Plus,
  Minus,
  Activity,
  XCircle,
  Check,
  AlertTriangle,
  BarChart3,
  Calculator,
  Calendar,
  Lightbulb,
  MessageCircle,
  X,
  Star,
  FileSearch,
  Brain,
  ListChecks,
  Mail,
  Send,
  FolderUp,
  Share2,
  Camera,
  Link2,
  Loader2,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { ScoreGauge, SCORE_MIN, SCORE_MAX } from '../../../components/ui/ScoreGauge';
import { BureauLogo } from '../../../components/ui/BureauLogo';
import DashboardWelcomeCard from '../../../components/onboarding/DashboardWelcomeCard';
import TrialStatusBanner from '../../../components/trial/TrialStatusBanner';
import CreditProfileSnapshot from '../../../components/dashboard/CreditProfileSnapshot';
import ShareSnapshotModal from '../../../components/dashboard/ShareSnapshotModal';
import { ReportHistorySelector, ScoreBreakdown } from '../../../components/credit-report';
import ProviderRefreshActionButton from '../../../components/import/ProviderRefreshActionButton';
import ProviderRefreshImportDialog from '../../../components/import/ProviderRefreshImportDialog';
import ReimportModal from '../../../components/credit-report/ReimportModal';
import { IMPORT_OUTCOMES, importHasSavedReport } from '../../../utils/creditImportStatus';
import {
  agencyOwnerNeedsEmailVerification,
  isAgencyWorkspaceUser,
  isPlatformStaffUser,
  applyPostAuthRedirect,
  AGENCY_DASHBOARD_PATH,
} from '../../../utils/authUtils';
import { isScopedSelfRepairSession } from '../../../utils/sessionScope';
import {
  buildAuthRequestConfig,
  resolveDashboardAuthGate,
  normalizeDashboardPayload,
} from './selfServeDashboardAuth';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const DASHBOARD_PRIMARY_TIMEOUT_MS = 30000;
const DASHBOARD_OPTIONAL_TIMEOUT_MS = 8000;
const DASHBOARD_LOADING_GUARD_MS = 35000;

const DASHBOARD_FETCH_COOLDOWN_MS = 15000;
const CHECKLIST_FETCH_COOLDOWN_MS = 15000;

/** Persist across remounts to avoid refetch storms when PortalContext toggles loading. */
const dashboardFetchTracker = {
  contextKey: null,
  lastFetchAt: 0,
  inFlight: false,
  cachedPayload: null,
};

const checklistFetchTracker = {
  contextKey: null,
  lastFetchAt: 0,
  inFlight: false,
  cachedProgress: null,
};

const buildDashboardContextKey = ({ user, isInSupportMode, supportContext, supportFetchReady }) => {
  if (!supportFetchReady) return null;
  const actorId = user?.user_id || 'unknown';
  if (isInSupportMode) {
    const effectiveUserId = supportContext?.effective_user_id;
    const effectivePortalType = supportContext?.effective_portal_type;
    if (!effectiveUserId || !effectivePortalType) return null;
    const contextId = supportContext?.context_id || 'support';
    return `${actorId}:${effectiveUserId}:${contextId}:${effectivePortalType}`;
  }
  return `${actorId}:${actorId}:self`;
};

const shouldApplyCachedDashboard = (currentDashboard, cachedPayload) => {
  if (!cachedPayload) return false;
  const cached = normalizeDashboardPayload(cachedPayload);
  const cachedUserId = cached.user_id || cached.score_history_scope?.effective_user_id || null;
  const currentUserId =
    currentDashboard?.user_id || currentDashboard?.score_history_scope?.effective_user_id || null;
  if (cachedUserId && currentUserId && cachedUserId !== currentUserId) return false;
  const cachedReportId = cached.latest_report_id || cached.report?.report_id || null;
  if (!currentDashboard) return true;
  const currentReportId =
    currentDashboard.latest_report_id || currentDashboard.report?.report_id || null;
  if (cachedReportId && currentReportId && cachedReportId !== currentReportId) return true;
  return false;
};

const canRunDashboardFetch = (contextKey, { force = false, reportId = null } = {}) => {
  if (!contextKey) return false;
  if (force || reportId) return true;
  if (dashboardFetchTracker.inFlight) return false;
  if (dashboardFetchTracker.contextKey !== contextKey) return true;
  return Date.now() - dashboardFetchTracker.lastFetchAt > DASHBOARD_FETCH_COOLDOWN_MS;
};

const canRunChecklistFetch = (contextKey, { force = false } = {}) => {
  if (!contextKey) return false;
  if (force) return true;
  if (checklistFetchTracker.inFlight) return false;
  if (checklistFetchTracker.contextKey !== contextKey) return true;
  return Date.now() - checklistFetchTracker.lastFetchAt > CHECKLIST_FETCH_COOLDOWN_MS;
};

// Icon mapping for checklist steps
const CHECKLIST_ICONS = {
  User,
  Link: Link2,
  Upload,
  FileSearch,
  Brain,
  FolderUp,
  Mail,
  Calculator,
};

// Success Checklist Modal Component - Backend-driven 8-step tracker
const SuccessChecklistModal = ({ isOpen, onClose, theme, navigate, t, isSupportMode = false }) => {
  const { token, sessionReady, isAuthenticated } = useAuth();
  const [checklistData, setChecklistData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && sessionReady && isAuthenticated) {
      fetchChecklist();
    }
  }, [isOpen, sessionReady, isAuthenticated, token]);

  const fetchChecklist = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/user/success-checklist`,
        buildAuthRequestConfig(token, DASHBOARD_OPTIONAL_TIMEOUT_MS)
      );
      setChecklistData(response.data);
    } catch (error) {
      console.error('Failed to fetch checklist:', error);
      // Fallback to empty state
      setChecklistData({
        total_steps: 8,
        completed_steps: 0,
        current_step: 'complete_profile',
        progress_percent: 0,
        steps: []
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const completedCount = checklistData?.completed_steps || 0;
  const totalSteps = checklistData?.total_steps || 8;
  const progress = checklistData?.progress_percent || 0;
  const steps = checklistData?.steps || [];
  const effectiveUserName = checklistData?.effective_user_name || 'Customer';
  const isReadOnly = isSupportMode || checklistData?.read_only === true;

  const handleStepClick = (route) => {
    if (isReadOnly) return;
    navigate(route);
    onClose();
  };

  const getStepIcon = (iconName) => {
    return CHECKLIST_ICONS[iconName] || FileSearch;
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal — flex column keeps footer visible on mobile; safe-area avoids clipped text */}
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-3 sm:p-4 pointer-events-none">
        <div 
          className="relative w-full max-w-lg max-h-[min(90dvh,640px)] flex flex-col overflow-hidden rounded-3xl pointer-events-auto"
          style={{
            background: theme.isDark 
              ? 'linear-gradient(180deg, #1A2F28 0%, #0F1F1A 100%)'
              : 'linear-gradient(180deg, #FFFFFF 0%, #F8FAF9 100%)',
            boxShadow: theme.isDark
              ? '0 25px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.08)'
              : '0 25px 80px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div 
            className="flex-shrink-0 px-4 sm:px-6 py-4 sm:py-5 border-b"
            style={{ borderColor: theme.isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ 
                    background: 'linear-gradient(135deg, #EAB23A 0%, #D4A12A 100%)',
                    boxShadow: '0 4px 12px rgba(234,178,58,0.3)'
                  }}
                >
                  <Star size={20} className="text-white" fill="white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold" style={{ color: theme.text.primary }}>
                    {isSupportMode
                      ? `${effectiveUserName}'s Success Checklist`
                      : t('dashboard.successChecklist')}
                  </h2>
                  <p className="text-xs" style={{ color: theme.text.muted }}>
                    {completedCount} {t('common.of')} {totalSteps} {t('dashboard.stepsComplete')}
                  </p>
                  {isReadOnly && (
                    <p className="text-xs mt-1" style={{ color: theme.text.muted }}>
                      Support view, read-only
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-full flex items-center justify-center transition-colors"
                style={{ 
                  background: theme.isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
                  color: theme.text.muted
                }}
              >
                <X size={18} />
              </button>
            </div>
            
            {/* Progress bar */}
            <div className="mt-4">
              <div 
                className="h-2 rounded-full overflow-hidden"
                style={{ background: theme.isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)' }}
              >
                <div 
                  className="h-full rounded-full transition-all duration-500"
                  style={{ 
                    width: `${progress}%`,
                    background: progress === 100 
                      ? 'linear-gradient(90deg, #10B981 0%, #059669 100%)'
                      : 'linear-gradient(90deg, #0FAC6C 0%, #0D9F64 100%)',
                    boxShadow: progress === 100 
                      ? '0 0 12px rgba(16,185,129,0.5)'
                      : '0 0 12px rgba(15,172,108,0.4)'
                  }}
                />
              </div>
            </div>
          </div>
          
          {/* Steps List */}
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 sm:px-6 py-3 sm:py-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin" style={{ color: theme.accent }} />
                <span className="ml-2 text-sm" style={{ color: theme.text.muted }}>Loading...</span>
              </div>
            ) : (
              <div className="relative">
                {/* Dashed vertical line behind icons */}
                <div 
                  className="absolute left-5 top-6 bottom-6 w-px"
                  style={{ 
                    background: `repeating-linear-gradient(
                      to bottom,
                      ${theme.isDark ? 'rgba(234,178,58,0.4)' : 'rgba(234,178,58,0.5)'} 0px,
                      ${theme.isDark ? 'rgba(234,178,58,0.4)' : 'rgba(234,178,58,0.5)'} 6px,
                      transparent 6px,
                      transparent 12px
                    )`
                  }}
                />
                
                {/* Steps */}
                <div className="space-y-1">
                  {steps.map((step, index) => {
                    const Icon = getStepIcon(step.icon);
                    const isCompleted = step.status === 'completed';
                    const isCurrent = step.status === 'current';
                    
                    return (
                      <button
                        key={step.key}
                        onClick={() => handleStepClick(step.route)}
                        disabled={isReadOnly}
                        className={`w-full flex items-center gap-4 p-3 rounded-xl transition-all duration-200 group text-left relative ${
                          isCurrent ? 'ring-2 ring-emerald-500/30' : ''
                        }`}
                        style={{
                          background: isCurrent 
                            ? theme.isDark ? 'rgba(15,172,108,0.08)' : 'rgba(15,172,108,0.05)'
                            : 'transparent',
                        }}
                        onMouseEnter={(e) => {
                          if (isReadOnly) return;
                          if (!isCurrent) {
                            e.currentTarget.style.background = theme.isDark 
                              ? 'rgba(255,255,255,0.05)' 
                              : 'rgba(0,0,0,0.03)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (isReadOnly) return;
                          if (!isCurrent) {
                            e.currentTarget.style.background = 'transparent';
                          }
                        }}
                        data-testid={`checklist-step-${step.key}`}
                      >
                        {/* Gold badge icon */}
                        <div 
                          className="relative z-10 w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-105"
                          style={{
                            background: isCompleted 
                              ? 'linear-gradient(135deg, #EAB23A 0%, #D4A12A 100%)'
                              : isCurrent
                                ? 'linear-gradient(135deg, #0FAC6C 0%, #0D9F64 100%)'
                                : theme.isDark 
                                  ? 'linear-gradient(135deg, #2A3F38 0%, #1F2F28 100%)'
                                  : 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)',
                            boxShadow: isCompleted 
                              ? '0 4px 12px rgba(234,178,58,0.35)'
                              : isCurrent
                                ? '0 4px 12px rgba(15,172,108,0.35)'
                                : 'none',
                            border: isCompleted || isCurrent
                              ? 'none'
                              : `1px solid ${theme.isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`
                          }}
                        >
                          {isCompleted ? (
                            <Check size={18} className="text-white" strokeWidth={3} />
                          ) : (
                            <Icon size={18} style={{ color: isCurrent ? '#FFFFFF' : theme.isDark ? '#8EDAB6' : '#0FAC6C' }} />
                          )}
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 
                              className="font-semibold text-sm"
                              style={{ 
                                color: isCompleted || isCurrent
                                  ? theme.text.primary 
                                  : theme.isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)'
                              }}
                            >
                              {step.label}
                            </h3>
                            {step.badge && (
                              <span 
                                className="px-2 py-0.5 text-[10px] font-semibold rounded-full"
                                style={{
                                  background: theme.isDark ? 'rgba(234,178,58,0.2)' : 'rgba(234,178,58,0.15)',
                                  color: '#D4A12A'
                                }}
                              >
                                {step.badge}
                              </span>
                            )}
                          </div>
                          <p 
                            className="text-xs mt-0.5 line-clamp-2"
                            style={{ color: theme.text.muted }}
                          >
                            {step.description}
                          </p>
                        </div>
                        
                        {/* Arrow */}
                        <ChevronRight 
                          size={18} 
                          className="flex-shrink-0 transition-transform group-hover:translate-x-1"
                          style={{ color: isCurrent ? theme.accent : theme.text.muted }}
                        />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div 
            className="flex-shrink-0 px-4 sm:px-6 pt-3 sm:pt-4 pb-[max(1rem,env(safe-area-inset-bottom,0px))] border-t"
            style={{ borderColor: theme.isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' }}
          >
            <p
              className="text-xs sm:text-sm text-center leading-normal sm:leading-relaxed py-0.5"
              style={{ color: theme.text.muted }}
            >
              {completedCount === totalSteps ? t('dashboard.checklistComplete') : t('dashboard.successChecklist')}
            </p>
          </div>
        </div>
      </div>
    </>
  );
};


// Layout: Bureau logo → Improvement text → Gauge → Animated Score → Status pill → Updated date
const THEMES = {
  light: {
    name: 'Light',
    background: {
      base: '#F7F9F8',
      secondary: '#F2FCF7',
    },
    card: {
      fill: 'rgba(255,255,255,0.85)',
      border: 'rgba(255,255,255,0.60)',
      contour: 'rgba(15,172,108,0.20)',
      shadow: '0 8px 32px rgba(16,24,40,0.08)',
      blur: 16,
    },
    text: { primary: '#111827', secondary: '#4B5563', muted: '#6B7280' },
    accent: '#0FAC6C',
    isDark: false,
  },
  dark: {
    name: 'Dark',
    background: {
      base: '#0B1F18',
      secondary: '#0F2A21',
    },
    card: {
      fill: 'rgba(15,45,35,0.75)',
      border: 'rgba(255,255,255,0.12)',
      contour: 'rgba(142,218,182,0.25)',
      shadow: '0 12px 40px rgba(0,0,0,0.25)',
      blur: 18,
    },
    text: { primary: '#F0FDF4', secondary: '#A7F3D0', muted: '#6EE7B7' },
    accent: '#56B988',
    isDark: true,
  },
};

// Premium Atmospheric Background - Wide Feathered Ovals (Cassis-inspired shape language)
const AnimatedOvalBackground = ({ isDark }) => {
  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 0 }}>
      {/* Base solid background */}
      <div 
        className="absolute inset-0"
        style={{ 
          background: isDark 
            ? '#0B1F18'
            : '#F7F9F8'
        }}
      />
      
      {/* === MAIN UPPER OVAL - Dominant luminous field === */}
      {/* Very wide horizontal feathered oval behind hero/dashboard area */}
      <div
        className="absolute"
        style={{
          width: '160%',
          height: '55%',
          top: '-15%',
          left: '-30%',
          background: isDark 
            ? 'radial-gradient(ellipse 100% 80% at 50% 60%, rgba(15,172,108,0.22) 0%, rgba(11,140,88,0.12) 35%, rgba(8,117,74,0.05) 60%, transparent 85%)'
            : 'radial-gradient(ellipse 100% 80% at 50% 60%, rgba(209,250,229,0.85) 0%, rgba(142,218,182,0.50) 35%, rgba(15,172,108,0.15) 60%, transparent 85%)',
          filter: 'blur(80px)',
          animation: 'ovalDriftMain 20s ease-in-out infinite',
        }}
      />
      
      {/* === SECONDARY LOWER OVAL - Softer support glow === */}
      {/* Broader, lower intensity behind content cards */}
      <div
        className="absolute"
        style={{
          width: '140%',
          height: '45%',
          bottom: '-20%',
          left: '-20%',
          background: isDark 
            ? 'radial-gradient(ellipse 100% 70% at 50% 30%, rgba(86,185,136,0.12) 0%, rgba(15,172,108,0.06) 40%, transparent 75%)'
            : 'radial-gradient(ellipse 100% 70% at 50% 30%, rgba(142,218,182,0.45) 0%, rgba(209,250,229,0.25) 40%, transparent 75%)',
          filter: 'blur(100px)',
          animation: 'ovalDriftSupport 24s ease-in-out infinite',
        }}
      />
      
      {/* === EDGE VIGNETTES - Frame the luminous center === */}
      
      {/* Left side falloff */}
      <div 
        className="absolute inset-y-0 left-0"
        style={{ 
          width: '25%',
          background: isDark
            ? 'linear-gradient(90deg, rgba(11,31,24,0.9) 0%, rgba(11,31,24,0.4) 50%, transparent 100%)'
            : 'linear-gradient(90deg, rgba(247,249,248,0.95) 0%, rgba(247,249,248,0.3) 50%, transparent 100%)'
        }}
      />
      
      {/* Right side falloff */}
      <div 
        className="absolute inset-y-0 right-0"
        style={{ 
          width: '25%',
          background: isDark
            ? 'linear-gradient(270deg, rgba(11,31,24,0.9) 0%, rgba(11,31,24,0.4) 50%, transparent 100%)'
            : 'linear-gradient(270deg, rgba(247,249,248,0.95) 0%, rgba(247,249,248,0.3) 50%, transparent 100%)'
        }}
      />
      
      {/* Bottom falloff */}
      <div 
        className="absolute inset-x-0 bottom-0"
        style={{ 
          height: '30%',
          background: isDark
            ? 'linear-gradient(0deg, rgba(11,31,24,0.85) 0%, rgba(11,31,24,0.3) 50%, transparent 100%)'
            : 'linear-gradient(0deg, rgba(247,249,248,0.9) 0%, rgba(247,249,248,0.2) 50%, transparent 100%)'
        }}
      />
      
      {/* Center-preserving vignette overlay */}
      <div 
        className="absolute inset-0"
        style={{ 
          background: isDark
            ? 'radial-gradient(ellipse 70% 55% at 50% 35%, transparent 0%, transparent 50%, rgba(11,31,24,0.5) 100%)'
            : 'radial-gradient(ellipse 70% 55% at 50% 35%, transparent 0%, transparent 50%, rgba(16,24,40,0.04) 100%)'
        }}
      />
      
      {/* Subtle animation keyframes - very slow, atmospheric drift */}
      <style>{`
        @keyframes ovalDriftMain {
          0%, 100% { 
            transform: translate(0, 0) scale(1); 
          }
          50% { 
            transform: translate(15px, 8px) scale(1.01); 
          }
        }
        @keyframes ovalDriftSupport {
          0%, 100% { 
            transform: translate(0, 0); 
            opacity: 0.95;
          }
          50% { 
            transform: translate(-10px, 5px); 
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

// Glass Card Component
const GlassCard = ({ children, className = '', theme, noPadding = false, solid = false }) => {
  const cardStyle = solid ? {
    background: theme.isDark ? 'rgba(20,40,32,0.95)' : 'rgba(255,255,255,0.95)',
    border: `1px solid ${theme.card.border}`,
    boxShadow: theme.card.shadow,
  } : {
    background: theme.card.fill,
    backdropFilter: `blur(${theme.card.blur}px) saturate(140%)`,
    WebkitBackdropFilter: `blur(${theme.card.blur}px) saturate(140%)`,
    border: `1px solid ${theme.card.border}`,
    boxShadow: `${theme.card.shadow}, inset 0 1px 0 ${theme.card.contour}`,
  };

  return (
    <div 
      className={`rounded-3xl overflow-hidden relative ${className}`}
      style={cardStyle}
    >
      {/* Contour glow effect */}
      <div 
        className="absolute inset-0 rounded-3xl pointer-events-none"
        style={{
          background: `linear-gradient(135deg, ${theme.card.contour} 0%, transparent 50%, transparent 100%)`,
          opacity: 0.5,
        }}
      />
      {/* Top highlight */}
      <div 
        className="absolute top-0 left-4 right-4 h-px pointer-events-none"
        style={{ background: `linear-gradient(90deg, transparent, ${theme.card.contour}, transparent)` }}
      />
      <div className={`relative ${noPadding ? '' : 'p-5'}`}>
        {children}
      </div>
    </div>
  );
};

// Welcome Progress Card - Clickable to open Success Checklist
const WelcomeProgressCard = ({ user, theme, tasksComplete = 9, totalTasks = 10, onClick, t }) => {
  const progress = (tasksComplete / totalTasks) * 100;
  const firstName = user?.name?.split(' ')[0] || 'there';
  
  return (
    <div 
      className="rounded-3xl p-6 relative overflow-hidden cursor-pointer transition-all duration-200 active:scale-[0.99]"
      style={{
        background: `linear-gradient(135deg, #08754A 0%, #0B8C58 50%, #0D9F64 100%)`,
        boxShadow: '0 20px 50px rgba(8,117,74,0.25)',
      }}
      onClick={onClick}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 24px 60px rgba(8,117,74,0.35)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 20px 50px rgba(8,117,74,0.25)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
      data-testid="welcome-progress-card"
    >
      {/* Subtle pattern overlay */}
      <div 
        className="absolute inset-0 opacity-5 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '20px 20px'
        }}
      />
      
      <div className="relative flex items-center justify-between">
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-white mb-1">
            {t('dashboard.welcome')} {firstName}!
          </h2>
          <p className="text-white/70 text-sm mb-4">{t('dashboard.welcomeMessage')}</p>
          
          {/* Progress bar */}
          <div className="max-w-md">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-white/80">{t('dashboard.stepsComplete')}</span>
              <span className="text-sm font-semibold text-white">{tasksComplete} {t('common.of')} {totalTasks}</span>
            </div>
            <div className="h-2.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.2)' }}>
              <div 
                className="h-full rounded-full transition-all duration-1000"
                style={{ 
                  width: `${progress}%`,
                  background: progress === 100 ? '#10B981' : '#EAB23A',
                  boxShadow: `0 0 12px ${progress === 100 ? '#10B981' : '#EAB23A'}50`
                }}
              />
            </div>
          </div>
        </div>
        
        <div className="hidden md:flex items-center">
          <Button 
            variant="ghost" 
            className="text-white/80 hover:text-white hover:bg-white/10 pointer-events-none"
          >
            {t('common.continue')}
            <ChevronRight size={18} className="ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// ScoreGauge imported from shared component
// SCORE_MIN, SCORE_MAX imported from shared component

// Calculate score change with proper state handling
const calculateScoreChange = (currentScore, baselineScore, initialScore) => {
  // Priority: initialScore > baselineScore (previous import)
  const baseline = initialScore || baselineScore;
  
  // No baseline exists
  if (!baseline || baseline === 0) {
    return { 
      delta: 0, 
      percentage: 0, 
      state: 'none', // no baseline available
      displayDelta: '0',
      displayPercent: '0.00%'
    };
  }
  
  const delta = currentScore - baseline;
  const percentage = (delta / baseline) * 100;
  
  // Determine state
  let state;
  if (delta > 0) {
    state = 'positive';
  } else if (delta < 0) {
    state = 'negative';
  } else {
    state = 'neutral'; // exactly 0 change
  }
  
  return {
    delta,
    percentage,
    state,
    displayDelta: delta > 0 ? `+${delta}` : `${delta}`,
    displayPercent: `${percentage >= 0 ? '+' : ''}${percentage.toFixed(2)}%`
  };
};

// Bureau Score Card with Logo - Premium Structure
// Layout: Bureau logo → Improvement text → Gauge → Animated Score → Status pill → Updated date
const BureauScoreCard = ({ 
  bureau, 
  score, 
  baselineScore, // previous imported score
  initialScore,  // first ever score
  lastUpdated,
  unifiedScoreChange, // unified API score change data
  theme, 
  animate,
  t
}) => {
  const isPlaceholderScore = (value) => value == null || value === 0 || value === 300;

  // Use unified score change if available, otherwise calculate from baseline
  let change;
  if (unifiedScoreChange) {
    // Map unified API data to expected format
    const direction = unifiedScoreChange.direction;
    let state = 'none';
    if (direction === 'up') state = 'positive';
    else if (direction === 'down') state = 'negative';
    else if (direction === 'baseline' || direction === 'first') state = 'baseline';
    else if (direction === 'none' && unifiedScoreChange.label === 'No change') state = 'neutral';
    
    change = {
      delta: unifiedScoreChange.point_change || 0,
      percentage: unifiedScoreChange.percent_change || 0,
      state: state,
      displayDelta: unifiedScoreChange.point_change !== null 
        ? (unifiedScoreChange.point_change > 0 ? `+${unifiedScoreChange.point_change}` : `${unifiedScoreChange.point_change}`)
        : null,
      displayPercent: unifiedScoreChange.percent_change !== null
        ? `${unifiedScoreChange.percent_change >= 0 ? '+' : ''}${unifiedScoreChange.percent_change.toFixed(2)}%`
        : null,
      label: unifiedScoreChange.label
    };
  } else {
    change = calculateScoreChange(score, baselineScore, initialScore);
  }
  
  // Previous score for animation starting point
  const previousScore = unifiedScoreChange?.previous_score || baselineScore || initialScore || SCORE_MIN;
  
  // Initialize display score to the START value so it animates in sync with gauge
  // If not animating, start at final score
  const resolvedScore = isPlaceholderScore(score) ? null : score;
  const resolvedPreviousScore = isPlaceholderScore(previousScore) ? SCORE_MIN : previousScore;
  const [displayScore, setDisplayScore] = useState(
    animate ? resolvedPreviousScore : (resolvedScore || SCORE_MIN)
  );

  const getStatus = (s) => {
    if (isPlaceholderScore(s)) return { labelKey: 'dashboard.noScore', color: '#6B7280' };
    if (s >= 750) return { labelKey: 'dashboard.excellent', color: '#0FAC6C' };
    if (s >= 700) return { labelKey: 'dashboard.good', color: '#56B988' };
    if (s >= 650) return { labelKey: 'dashboard.fair', color: '#EAB23A' };
    if (s >= 500) return { labelKey: 'dashboard.poor', color: '#F97316' };
    return { labelKey: 'dashboard.veryPoor', color: '#EF4444' };
  };
  
  const status = getStatus(resolvedScore);
  
  // Change display styling based on state
  const getChangeStyles = () => {
    switch (change.state) {
      case 'positive':
        return { color: '#10B981', bgColor: 'rgba(16,185,129,0.12)' };
      case 'negative':
        return { color: '#EF4444', bgColor: 'rgba(239,68,68,0.12)' };
      case 'baseline':
      case 'first':
        return { color: theme.isDark ? '#8FE0B7' : '#0B8C58', bgColor: theme.isDark ? 'rgba(143,224,183,0.12)' : 'rgba(11,140,88,0.1)' };
      case 'neutral':
      case 'none':
      default:
        // Muted emerald-gray for neutral
        return { color: theme.isDark ? '#8FE0B7' : '#0B8C58', bgColor: theme.isDark ? 'rgba(143,224,183,0.1)' : 'rgba(11,140,88,0.08)' };
    }
  };
  
  const changeStyles = getChangeStyles();
  
  // Memoized callback to handle animated score updates from gauge
  const handleScoreUpdate = useCallback((animatedScore) => {
    setDisplayScore(animatedScore);
  }, []);
  
  // Render the change badge - Compact marketing-optimized style
  const renderChangeBadge = () => {
    // Base styles for the compact badge
    const badgeBaseStyles = {
      background: changeStyles.bgColor,
      color: changeStyles.color,
      boxShadow: change.state === 'positive' 
        ? '0 1px 4px rgba(16,185,129,0.2)' 
        : change.state === 'negative' 
          ? '0 1px 4px rgba(239,68,68,0.2)'
          : change.state === 'baseline' || change.state === 'first'
            ? '0 1px 4px rgba(15,172,108,0.15)'
            : '0 1px 3px rgba(0,0,0,0.08)',
    };

    if (change.state === 'baseline' || change.state === 'first' || change.label === 'First Report') {
      return (
        <div
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full"
          style={badgeBaseStyles}
        >
          <Calendar size={15} strokeWidth={2.5} />
          <span className="text-[15px] md:text-[16px] font-extrabold tracking-tight whitespace-nowrap">
            {change.label || lastUpdated || 'Imported'}
          </span>
        </div>
      );
    }

    if (!change.displayDelta && (change.state === 'neutral' || change.state === 'none')) {
      return (
        <div
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full"
          style={badgeBaseStyles}
        >
          <Minus size={15} strokeWidth={2.5} />
          <span className="text-[15px] md:text-[16px] font-extrabold tracking-tight whitespace-nowrap">
            No change
          </span>
        </div>
      );
    }
    
    // Normal change display - compact with larger text
    return (
      <div 
        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full"
        style={badgeBaseStyles}
      >
        {change.state === 'positive' && <TrendingUp size={15} strokeWidth={2.5} />}
        {change.state === 'negative' && <TrendingDown size={15} strokeWidth={2.5} />}
        {(change.state === 'neutral' || change.state === 'none') && <Minus size={15} strokeWidth={2.5} />}
        <span className="text-[15px] md:text-[16px] font-extrabold tracking-tight whitespace-nowrap">
          {change.displayDelta !== null ? change.displayDelta : '0'} pts
        </span>
        <span className="text-[15px] md:text-[16px] font-bold opacity-80 whitespace-nowrap">
          ({change.displayPercent || '0.00%'})
        </span>
      </div>
    );
  };
  
  return (
    <GlassCard theme={theme} className="h-full" data-testid={`bureau-card-${bureau.toLowerCase()}`}>
      {/* 1. Bureau Logo - Centered */}
      <div className="flex items-center justify-center mb-3">
        <div className="h-8 flex items-center">
          <BureauLogo
            bureau={bureau}
            isDark={theme.isDark}
            height="2rem"
            maxWidth="120px"
          />
        </div>
      </div>
      
      {/* 2. Improvement Text - Centered */}
      <div className="flex justify-center mb-3">
        {renderChangeBadge()}
      </div>
      
      {/* 3. Semi-circle Gauge with Score Inside */}
      <div className="relative">
        <div className="flex justify-center">
          <ScoreGauge 
            score={resolvedScore || SCORE_MIN} 
            previousScore={resolvedPreviousScore}
            size={180} 
            animate={animate} 
            theme={theme}
            onScoreUpdate={handleScoreUpdate}
          />
        </div>
        
        {/* Score Block - Overlaps into the gauge opening */}
        <div 
          className="absolute left-0 right-0 flex flex-col items-center pointer-events-none"
          style={{ top: '50px' }}
        >
          {/* Large Animated Score Number */}
          <p 
            className="text-4xl font-bold tracking-tight leading-none pointer-events-auto"
            style={{ color: theme.text.primary }}
            data-testid={`score-value-${bureau.toLowerCase()}`}
          >
            {isPlaceholderScore(displayScore) || isPlaceholderScore(resolvedScore) ? '--' : displayScore}
          </p>
          
          {/* Status Pill - Tight below score */}
          <div 
            className="mt-0.5 px-3 py-0.5 rounded-full text-xs font-semibold pointer-events-auto"
            style={{ 
              background: `${status.color}15`,
              color: status.color
            }}
          >
            {t(status.labelKey)}
          </div>
        </div>
      </div>
      
      {/* 6. Updated Date - Centered */}
      {lastUpdated && (
        <div className="flex justify-center mt-1 pt-2 border-t" style={{ borderColor: theme.card.border }}>
          <span className="text-xs" style={{ color: theme.text.muted }}>
            {t('dashboard.lastUpdated')} {typeof lastUpdated === 'string' && lastUpdated.includes(',') 
              ? lastUpdated 
              : new Date(lastUpdated).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
        </div>
      )}
    </GlassCard>
  );
};

// KPI Card - Perfectly Centered Layout (icon → label → value)
const KPICard = ({ icon: Icon, label, value, color, theme }) => (
  <GlassCard theme={theme} noPadding className="h-full min-w-0">
    <div className="flex flex-col items-center justify-center text-center py-4 sm:py-5 px-2 sm:px-3 h-full w-full min-w-0">
      <div 
        className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center mx-auto mb-1.5 sm:mb-2"
        style={{ background: `${color}15` }}
      >
        <Icon size={20} style={{ color }} />
      </div>
      <p className="text-[10px] sm:text-xs mb-1 w-full line-clamp-2 leading-snug" style={{ color: theme.text.muted }}>{label}</p>
      <p className="text-xl sm:text-2xl font-bold leading-none w-full tabular-nums" style={{ color: theme.text.primary }}>{value}</p>
    </div>
  </GlassCard>
);

// Module Summary Card
const ModuleSummaryCard = ({ title, icon: Icon, to, items, accentColor, theme }) => (
  <Link to={to}>
    <GlassCard theme={theme} className="h-full hover:scale-[1.02] transition-transform duration-200">
      <div className="flex items-center gap-3 mb-4">
        <div 
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ background: `${accentColor}20` }}
        >
          <Icon size={20} style={{ color: accentColor }} />
        </div>
        <h3 className="font-semibold" style={{ color: theme.text.primary }}>{title}</h3>
        <ChevronRight size={16} className="ml-auto" style={{ color: theme.text.muted }} />
      </div>
      <div className="space-y-2">
        {items.map((item, idx) => (
          <div key={idx} className="flex items-center justify-between text-sm">
            <span style={{ color: theme.text.muted }}>{item.label}</span>
            <span className="font-medium" style={{ color: item.highlight ? accentColor : theme.text.secondary }}>
              {item.value}
            </span>
          </div>
        ))}
      </div>
    </GlassCard>
  </Link>
);

// AI Insights Carousel
const AIInsightsCarousel = ({ theme, insights, t, strictNegativeCount, strictRemainingCount, scores, dispute_workflow }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef(null);
  
  // Build dynamic insights based on user's actual data
  const dynamicInsights = useMemo(() => {
    const result = [];
    
    // Generate insights based on real data
    if (strictNegativeCount > 0) {
      result.push({
        icon: AlertCircle,
        text: t('dashboard.aiInsightNegativeItems', { count: strictNegativeCount }),
        type: 'action'
      });
    }
    
    if (scores?.transunion && scores?.experian && scores?.equifax) {
      const avgScore = Math.round((scores.transunion + scores.experian + scores.equifax) / 3);
      if (avgScore < 580) {
        result.push({
          icon: TrendingUp,
          text: t('dashboard.aiInsightLowScore', { score: avgScore }),
          type: 'tip'
        });
      } else if (avgScore >= 700) {
        result.push({
          icon: Target,
          text: t('dashboard.aiInsightGoodScore', { score: avgScore }),
          type: 'insight'
        });
      }
    }
    
    if (strictRemainingCount > 0) {
      result.push({
        icon: Shield,
        text: t('dashboard.aiInsightRemaining', { count: strictRemainingCount }),
        type: 'reminder'
      });
    }
    
    if (dispute_workflow?.candidates_identified > 0 && dispute_workflow?.candidates_selected === 0) {
      result.push({
        icon: FileText,
        text: t('dashboard.aiInsightCandidates', { count: dispute_workflow.candidates_identified }),
        type: 'action'
      });
    }
    
    // If no dynamic insights, use generic ones
    if (result.length === 0) {
      result.push(
        { icon: TrendingUp, text: t('dashboard.aiInsightTip1'), type: 'tip' },
        { icon: Target, text: t('dashboard.aiInsightTip2'), type: 'action' },
        { icon: Shield, text: t('dashboard.aiInsightTip5'), type: 'tip' }
      );
    }
    
    return result;
  }, [t, strictNegativeCount, strictRemainingCount, scores, dispute_workflow]);
  
  const displayInsights = insights?.length > 0 ? insights : dynamicInsights;
  
  useEffect(() => {
    if (!isPaused) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % displayInsights.length);
      }, 7000);
    }
    return () => clearInterval(intervalRef.current);
  }, [isPaused, displayInsights.length]);
  
  const goTo = (idx) => setCurrentIndex(idx);
  const goNext = () => setCurrentIndex((prev) => (prev + 1) % displayInsights.length);
  const goPrev = () => setCurrentIndex((prev) => (prev - 1 + displayInsights.length) % displayInsights.length);
  
  const current = displayInsights[currentIndex];
  const IconComponent = current.icon;
  
  return (
    <GlassCard 
      theme={theme} 
      className="relative"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={18} style={{ color: theme.accent }} />
        <span className="text-sm font-semibold" style={{ color: theme.text.primary }}>
          {t('dashboard.aiInsightsTitle')}
        </span>
      </div>
      
      {/* Insight Content */}
      <div className="min-h-[60px] flex items-center">
        <div 
          className="flex items-start gap-3 transition-opacity duration-300"
          key={currentIndex}
        >
          <div 
            className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: `${theme.accent}20` }}
          >
            <IconComponent size={18} style={{ color: theme.accent }} />
          </div>
          <p className="text-sm leading-relaxed" style={{ color: theme.text.secondary }}>
            {current.text}
          </p>
        </div>
      </div>
      
      {/* Controls */}
      <div className="flex items-center justify-between mt-4">
        <div className="flex gap-1.5">
          {displayInsights.map((_, idx) => (
            <button
              key={idx}
              onClick={() => goTo(idx)}
              className="w-2 h-2 rounded-full transition-all duration-200"
              style={{ 
                background: idx === currentIndex ? theme.accent : `${theme.accent}30`,
                transform: idx === currentIndex ? 'scale(1.2)' : 'scale(1)'
              }}
            />
          ))}
        </div>
        <div className="flex gap-1">
          <button 
            onClick={goPrev}
            className="p-1.5 rounded-lg transition-colors"
            style={{ color: theme.text.muted }}
          >
            <ChevronLeft size={16} />
          </button>
          <button 
            onClick={goNext}
            className="p-1.5 rounded-lg transition-colors"
            style={{ color: theme.text.muted }}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </GlassCard>
  );
};

// Recent Activity
const RecentActivityPanel = ({ activity, theme, t }) => {
  // Use real activity if available, otherwise show empty state
  const data = activity?.length > 0 ? activity : [];
  
  if (data.length === 0) {
    return (
      <GlassCard theme={theme} solid noPadding>
        <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: theme.card.border }}>
          <h3 className="font-semibold" style={{ color: theme.text.primary }}>{t('dashboard.recentActivity')}</h3>
          <Activity size={18} style={{ color: theme.text.muted }} />
        </div>
        <div className="p-8 text-center">
          <p className="text-sm" style={{ color: theme.text.muted }}>{t('dashboard.noRecentActivity')}</p>
        </div>
      </GlassCard>
    );
  }
  
  return (
    <GlassCard theme={theme} solid noPadding>
      <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: theme.card.border }}>
        <h3 className="font-semibold" style={{ color: theme.text.primary }}>{t('dashboard.recentActivity')}</h3>
        <Activity size={18} style={{ color: theme.text.muted }} />
      </div>
      <div className="divide-y" style={{ borderColor: theme.card.border }}>
        {data.map((item, idx) => {
          const IconComponent = item.icon || Activity;
          const eventTime = item.created_at ? new Date(item.created_at).toLocaleString() : item.time;
          return (
            <div key={idx} className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3 min-w-0">
              <div className="flex items-start gap-3 min-w-0 flex-1">
                <div 
                  className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: `${item.color || theme.accent}20` }}
                >
                  <IconComponent size={16} style={{ color: item.color || theme.accent }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium break-words" style={{ color: theme.text.primary }}>{item.event_type || item.title}</p>
                  <p className="text-xs break-words" style={{ color: theme.text.muted }}>{item.details || item.description}</p>
                </div>
              </div>
              <span className="text-xs pl-11 sm:pl-0 sm:ml-auto shrink-0 tabular-nums" style={{ color: theme.text.muted }}>{eventTime}</span>
            </div>
          );
        })}
      </div>
    </GlassCard>
  );
};

/**
 * Self-Serve Dashboard - Premium Glass Interface
 */
const SelfServeDashboard = () => {
  if (process.env.NODE_ENV === 'development') {
    console.count('[SelfServeDashboard] render');
  }

  const navigate = useNavigate();
  const { token, user, sessionReady, authState, isAuthenticated, getAuthHeaders } = useAuth();
  const {
    supportContext,
    isInSupportMode,
    contextReady,
    isPlatformOwner,
  } = useSupportContext();
  const { t } = useI18n();
  const { 
    state: onboardingState, 
    loading: onboardingLoading, 
    isAutoStarter,
    checkDashboardAccess 
  } = useOnboarding();
  const [dashboard, setDashboard] = useState(null);
  const [unifiedSummary, setUnifiedSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showWelcomeCard, setShowWelcomeCard] = useState(false);
  const [accessChecked, setAccessChecked] = useState(false);
  const [animateScores, setAnimateScores] = useState(true);
  const [showChecklist, setShowChecklist] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [isViewingHistorical, setIsViewingHistorical] = useState(false);
  const [showSnapshotModal, setShowSnapshotModal] = useState(false);
  const [refreshImportId, setRefreshImportId] = useState(null);
  const [refreshImportSession, setRefreshImportSession] = useState(null);
  const [showReimportModal, setShowReimportModal] = useState(false);
  const [checklistProgress, setChecklistProgress] = useState({ completed: 0, total: 8 });
  const dashboardContextKeyRef = useRef(null);
  const lastAppliedCacheKeyRef = useRef(null);

  const supportFetchReady = useMemo(() => {
    if (!isPlatformOwner) return true;
    if (!contextReady) return false;
    if (!isInSupportMode) return true;
    return Boolean(
      supportContext?.effective_user_id && supportContext?.effective_portal_type
    );
  }, [
    isPlatformOwner,
    contextReady,
    isInSupportMode,
    supportContext?.effective_user_id,
    supportContext?.effective_portal_type,
  ]);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    console.count('[SelfServeDashboard] mount');
    return () => console.count('[SelfServeDashboard] unmount');
  }, []);

  const agencyWorkspaceUser = isAgencyWorkspaceUser(user) && !isScopedSelfRepairSession(user);

  // DOMAIN-AWARE ROUTING: redirect before dashboard API calls
  useLayoutEffect(() => {
    if (!user) return;
    // Scoped Self Repair must never chase an agency redirect.
    if (isScopedSelfRepairSession(user)) {
      return;
    }
    if (agencyWorkspaceUser) {
      if (agencyOwnerNeedsEmailVerification(user)) {
        navigate('/verify-email', {
          replace: true,
          state: {
            email: user.email,
            role: 'agency_owner',
            redirectTo: AGENCY_DASHBOARD_PATH,
            agencyId: user.agency_id,
          },
        });
        return;
      }
      applyPostAuthRedirect(user, navigate, AGENCY_DASHBOARD_PATH);
      return;
    }

    if (isPlatformOwner && !contextReady) return;
    if (isInSupportMode) return;

    if (isPlatformStaffUser(user)) {
      applyPostAuthRedirect(user, navigate);
    }
  }, [user, agencyWorkspaceUser, navigate, isInSupportMode, isPlatformOwner, contextReady]);

  // Use theme from layout context
  const themeContext = useTheme();
  const resolvedTheme = themeContext?.resolvedTheme || 'light';
  const theme = THEMES[resolvedTheme] || THEMES.light;

  const effectiveClientName = isInSupportMode 
    ? (supportContext?.effective_user_name || 'Customer')
    : null;
  
  const isViewingAsSupport = isInSupportMode && supportContext?.effective_user_id;

  useEffect(() => {
    if (isInSupportMode || isPlatformOwner) {
      setShowWelcomeCard(false);
      return;
    }
    const isSelfServe = user?.role === 'self_repair_customer' && !user?.managed_by;
    const welcomeDismissed = localStorage.getItem('fundelite_welcome_dismissed') === 'true';
    if (isSelfServe && user?.show_dashboard_welcome && !welcomeDismissed) {
      setShowWelcomeCard(true);
    }
  }, [
    user?.role,
    user?.managed_by,
    user?.show_dashboard_welcome,
    user?.onboarding_completed,
    isInSupportMode,
    isPlatformOwner,
  ]);

  useEffect(() => {
    const checkAccess = async () => {
      if (!isAutoStarter || isInSupportMode) {
        setAccessChecked(true);
        return;
      }
      if (onboardingLoading) return;
      try {
        const accessResult = await checkDashboardAccess();
        if (!accessResult?.can_access) {
          const redirectStep = onboardingState?.current_step || 'welcome_terms';
          navigate(`/onboarding?step=${redirectStep}`, { replace: true });
          setAccessChecked(true);
          return;
        }
        setAccessChecked(true);
      } catch (err) {
        console.error('Failed to check dashboard access:', err);
        setAccessChecked(true);
      }
    };
    checkAccess();
  }, [isAutoStarter, onboardingLoading, onboardingState, isInSupportMode, checkDashboardAccess, navigate]);

  // Fallback: never block dashboard forever on onboarding/access gating
  useEffect(() => {
    if (!isAutoStarter || accessChecked || isInSupportMode) return;
    const timeout = setTimeout(() => {
      console.warn('[SelfServeDashboard] Access check timeout — allowing dashboard render');
      setAccessChecked(true);
    }, 8000);
    return () => clearTimeout(timeout);
  }, [isAutoStarter, accessChecked, isInSupportMode]);

  const handleDismissWelcome = async () => {
    setShowWelcomeCard(false);
    localStorage.setItem('fundelite_welcome_dismissed', 'true');
    try {
      await axios.post(
        `${API}/onboarding/dismiss-welcome`,
        {},
        buildAuthRequestConfig(token, DASHBOARD_OPTIONAL_TIMEOUT_MS)
      );
    } catch (err) {
      console.error('Failed to dismiss welcome:', err);
    }
  };

  const dashboardContextKey = useMemo(
    () => buildDashboardContextKey({ user, isInSupportMode, supportContext, supportFetchReady }),
    [
      user?.user_id,
      isInSupportMode,
      supportFetchReady,
      supportContext?.context_id,
      supportContext?.effective_user_id,
      supportContext?.effective_portal_type,
    ]
  );

  useEffect(() => {
    if (!dashboardContextKey) return;
    if (
      lastAppliedCacheKeyRef.current
      && lastAppliedCacheKeyRef.current !== dashboardContextKey
    ) {
      setDashboard((prev) => (prev ? { ...prev, score_history: [] } : prev));
      dashboardFetchTracker.cachedPayload = null;
      dashboardFetchTracker.contextKey = null;
      dashboardFetchTracker.lastFetchAt = 0;
    }
  }, [dashboardContextKey]);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    if (dashboardContextKeyRef.current !== dashboardContextKey) {
      console.log(
        '[SelfServeDashboard] dashboardContextKey',
        dashboardContextKeyRef.current,
        '->',
        dashboardContextKey
      );
      dashboardContextKeyRef.current = dashboardContextKey;
    }
  }, [dashboardContextKey]);

  const fetchUnifiedSummary = async (unifiedUrl, normalizedReportId) => {
    try {
      const response = await axios.get(
        unifiedUrl,
        buildAuthRequestConfig(token, DASHBOARD_OPTIONAL_TIMEOUT_MS)
      );
      const data = response.data;
      if (data) {
        if (data.dashboard_real_counts_debug) {
          console.info('[dashboard_real_counts]', data.dashboard_real_counts_debug);
        }
        setUnifiedSummary(data);
        setIsViewingHistorical(data.is_latest === false);
      }
    } catch (err) {
      setUnifiedSummary(null);
      if (process.env.NODE_ENV === 'development') {
        console.warn('[SelfServeDashboard] Unified summary skipped:', err.message);
      }
    }
  };

  const fetchDashboard = async (reportId = null, { force = false } = {}) => {
    if (process.env.NODE_ENV === 'development') {
      console.count('[SelfServeDashboard] fetchDashboard called');
    }

    const contextKey = dashboardContextKey;
    if (!contextKey || !supportFetchReady) {
      return;
    }

    if (!canRunDashboardFetch(contextKey, { force, reportId })) {
      if (
        !reportId &&
        dashboardFetchTracker.cachedPayload &&
        dashboardFetchTracker.contextKey === contextKey &&
        shouldApplyCachedDashboard(dashboard, dashboardFetchTracker.cachedPayload)
      ) {
        setDashboard(normalizeDashboardPayload(dashboardFetchTracker.cachedPayload));
        setLoading(false);
        setError(null);
      } else if (!dashboardFetchTracker.inFlight) {
        // Cooldown / skip without cache must not leave the page spinning forever.
        setLoading(false);
      }
      return;
    }

    dashboardFetchTracker.inFlight = true;
    const hasCachedPayload =
      !reportId &&
      dashboardFetchTracker.cachedPayload &&
      dashboardFetchTracker.contextKey === contextKey;
    if (!hasCachedPayload) {
      setLoading(true);
    }
    setError(null);
    if (!hasCachedPayload) {
      setAnimateScores(true);
    }

    let dashboardUrl = `${API}/portal/self-serve/dashboard`;
    let unifiedUrl = `${API}/dashboard/unified-summary`;
    let normalizedReportId = null;

    if (reportId) {
      if (typeof reportId === 'string') {
        normalizedReportId = reportId;
      } else if (typeof reportId === 'object') {
        normalizedReportId = reportId.report_id || reportId.id || reportId._id || null;
        if (process.env.NODE_ENV === 'development') {
          console.warn('[SelfServeDashboard] reportId was an object, extracted:', normalizedReportId);
        }
      }

      if (normalizedReportId && typeof normalizedReportId === 'string' && !normalizedReportId.includes('[object')) {
        dashboardUrl += `?report_id=${normalizedReportId}`;
        unifiedUrl += `?report_id=${normalizedReportId}`;
      }
    }

    try {
      const dashboardResponse = await axios.get(
        dashboardUrl,
        buildAuthRequestConfig(token, DASHBOARD_PRIMARY_TIMEOUT_MS)
      );

      // 204 No Content (or empty body) = completed empty dashboard, not pending.
      const payload =
        dashboardResponse.status === 204
          ? { has_case: false, empty: true, portal: 'self_serve_portal' }
          : dashboardResponse.data;
      const normalized = normalizeDashboardPayload(payload);
      if (process.env.NODE_ENV === 'development' && normalized.score_history_scope) {
        console.info('[SelfServeDashboard] score_history_scope', normalized.score_history_scope);
      }
      setDashboard(normalized);
      dashboardFetchTracker.contextKey = contextKey;
      dashboardFetchTracker.lastFetchAt = Date.now();
      dashboardFetchTracker.cachedPayload = normalized;
      setLoading(false);
      setError(null);
      lastAppliedCacheKeyRef.current = contextKey;

      if (!normalized.has_case) {
        setIsViewingHistorical(false);
      } else if (normalizedReportId && normalized.report?.report_id !== normalized.latest_report_id) {
        setIsViewingHistorical(true);
      } else {
        setIsViewingHistorical(false);
      }

      fetchUnifiedSummary(unifiedUrl, normalizedReportId);
    } catch (err) {
      console.error('[SelfServeDashboard] Primary dashboard fetch failed:', err);
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      let message;
      if (typeof detail === 'string' && detail.trim()) {
        message = status ? `Dashboard error (${status}): ${detail}` : detail;
      } else if (err.code === 'ECONNABORTED') {
        message = 'Dashboard request timed out. Please try again.';
      } else if (status === 401) {
        message = 'Dashboard error (401): Authentication required. Please sign in again.';
      } else if (status === 403) {
        message = 'Dashboard error (403): You do not have access to this dashboard.';
      } else if (status === 404) {
        message = 'Dashboard error (404): Dashboard endpoint was not found.';
      } else if (status >= 500) {
        message = `Dashboard error (${status}): Server error while loading dashboard.`;
      } else if (status) {
        message = `Dashboard error (${status}): Failed to load dashboard.`;
      } else {
        message = err.message || 'Failed to load dashboard';
      }
      if (
        process.env.NODE_ENV === 'development'
        || process.env.REACT_APP_VERCEL_ENV === 'preview'
        || (typeof window !== 'undefined' && window.location.hostname.endsWith('.vercel.app'))
      ) {
        console.info('[AUTH_DIAG] dashboard_fetch', {
          frontendOrigin: typeof window !== 'undefined' ? window.location.origin : null,
          endpoint: dashboardUrl,
          method: 'GET',
          status: status || null,
          aborted: err.code === 'ECONNABORTED',
        });
      }
      setError(message);
      setLoading(false);
    } finally {
      dashboardFetchTracker.inFlight = false;
    }
  };

  // Handle report selection from ReportHistorySelector
  const handleReportChange = useCallback((reportId) => {
    // Normalize reportId - extract string ID if object was passed
    let normalizedId = null;
    if (reportId) {
      if (typeof reportId === 'string') {
        normalizedId = reportId;
      } else if (typeof reportId === 'object') {
        normalizedId = reportId.report_id || reportId.id || reportId._id || null;
      }
    }

    setSelectedReportId(normalizedId);
    fetchDashboard(normalizedId, { force: true });
  }, [dashboardContextKey]);

  const effectiveUserId = dashboard?.user_id
    || (isInSupportMode ? supportContext?.effective_user_id : user?.user_id);

  const handleViewCreditReport = useCallback(() => {
    const reportId = selectedReportId || dashboard?.report?.report_id;
    navigate('/credit-report', {
      state: reportId ? { reportId, refresh: true } : { refresh: true },
    });
  }, [navigate, selectedReportId, dashboard?.report?.report_id]);

  const handleRefreshImportComplete = useCallback(async (result) => {
    setRefreshImportId(null);
    setRefreshImportSession(null);
    await fetchDashboard(selectedReportId, { force: true });
    const reportId = result?.report_id || selectedReportId || dashboard?.report?.report_id;
    if (result?.needs_review) {
      toast.info('Report imported. Please review your credit report.');
    } else {
      toast.success('Latest credit report imported successfully.');
    }
    navigate('/credit-report', {
      state: {
        refresh: true,
        reportId,
        importOutcome: result?.outcome || IMPORT_OUTCOMES.NEW_IMPORT_COMPLETED,
        importId: result?.import_id,
      },
    });
  }, [fetchDashboard, navigate, selectedReportId, dashboard?.report?.report_id]);

  const handleRefreshImportError = useCallback((error) => {
    const details = typeof error === 'object' ? (error?.details || error) : null;
    if (importHasSavedReport(details)) {
      handleRefreshImportComplete({
        report_id: details.report_id,
        outcome: IMPORT_OUTCOMES.REVIEW_NEEDED,
        needs_review: true,
        import_id: details.import_id,
      });
      return 'handled';
    }
    return undefined;
  }, [handleRefreshImportComplete]);

  useEffect(() => {
    if (agencyWorkspaceUser) {
      return;
    }

    // Cookie-authenticated sessions are enough — do not require a client-readable JWT.
    const authGate = resolveDashboardAuthGate({
      sessionReady,
      isAuthenticated,
      user,
      authState,
    });
    if (authGate.action !== 'fetch') {
      if (authGate.action === 'reject') {
        console.warn('[SelfServeDashboard] Not authenticated — cannot fetch dashboard');
        setLoading(false);
        setError('Authentication required. Please log in.');
      } else {
        setLoading(true);
      }
      return;
    }

    if (!supportFetchReady || !dashboardContextKey) {
      setLoading(true);
      return;
    }

    const cacheKey = dashboardContextKey;
    if (
      dashboardFetchTracker.cachedPayload &&
      dashboardFetchTracker.contextKey === cacheKey &&
      canRunDashboardFetch(cacheKey) === false &&
      shouldApplyCachedDashboard(dashboard, dashboardFetchTracker.cachedPayload) &&
      lastAppliedCacheKeyRef.current !== cacheKey
    ) {
      setDashboard(normalizeDashboardPayload(dashboardFetchTracker.cachedPayload));
      setLoading(false);
      setError(null);
      lastAppliedCacheKeyRef.current = cacheKey;
      return;
    }

    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sessionReady/auth gate; token optional
  }, [dashboardContextKey, sessionReady, isAuthenticated, authState, user?.user_id, supportFetchReady]);

  // Fetch checklist progress (cooldown + cache across remounts)
  useEffect(() => {
    if (isPlatformOwner && !isInSupportMode) {
      return;
    }

    const fetchChecklistProgress = async () => {
      if (!sessionReady || !isAuthenticated || !supportFetchReady || !dashboardContextKey) return;

      if (process.env.NODE_ENV === 'development') {
        console.count('[SelfServeDashboard] fetchChecklist called');
      }

      const contextKey = dashboardContextKey;
      if (!canRunChecklistFetch(contextKey)) {
        if (
          checklistFetchTracker.cachedProgress &&
          checklistFetchTracker.contextKey === contextKey
        ) {
          setChecklistProgress(checklistFetchTracker.cachedProgress);
        }
        return;
      }

      checklistFetchTracker.inFlight = true;
      try {
        const response = await axios.get(
          `${API}/user/success-checklist`,
          buildAuthRequestConfig(token, DASHBOARD_OPTIONAL_TIMEOUT_MS)
        );
        const progress = {
          completed: response.data.completed_steps || 0,
          total: response.data.total_steps || 8,
        };
        setChecklistProgress(progress);
        checklistFetchTracker.contextKey = contextKey;
        checklistFetchTracker.lastFetchAt = Date.now();
        checklistFetchTracker.cachedProgress = progress;
      } catch (error) {
        console.error('Failed to fetch checklist progress:', error);
      } finally {
        checklistFetchTracker.inFlight = false;
      }
    };
    fetchChecklistProgress();
  }, [dashboardContextKey, sessionReady, isAuthenticated, token, supportFetchReady, isInSupportMode, isPlatformOwner]);

  // Disable score animation after initial load
  useEffect(() => {
    if (!loading && dashboard) {
      const timer = setTimeout(() => setAnimateScores(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [loading, dashboard]);


  // Last-resort guard if primary fetch never settles (should not fire after axios timeout)
  useEffect(() => {
    if (!loading || dashboard || error) return;

    const timeout = setTimeout(() => {
      if (dashboard || error) return;
      console.error('[SelfServeDashboard] Loading guard timeout - clearing loading state');
      dashboardFetchTracker.inFlight = false;
      setLoading(false);
      // Prefer a concrete stuck-state message over pretending the API timed out
      // when axios already completed or never started.
      setError((prev) => prev || (
        !supportFetchReady
          ? 'Dashboard could not start loading because session context was not ready. Please refresh the page.'
          : 'Dashboard loading did not complete. If this continues, refresh the page or sign in again.'
      ));
    }, DASHBOARD_LOADING_GUARD_MS);

    return () => clearTimeout(timeout);
  }, [loading, dashboard, error, supportFetchReady]);

  if (!supportFetchReady || loading || (isAutoStarter && !accessChecked)) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: theme.background.base }}>
        <div className="flex flex-col items-center gap-3">
          <div 
            className="w-10 h-10 border-3 rounded-full animate-spin"
            style={{ borderColor: `${theme.accent}30`, borderTopColor: theme.accent }}
          />
          <p className="text-sm" style={{ color: theme.text.muted }}>
            {isInSupportMode && effectiveClientName
              ? `Loading ${effectiveClientName}'s dashboard…`
              : t('common.loading')}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen p-6" style={{ background: theme.background.base }}>
        <GlassCard theme={theme}>
          <div className="flex items-center gap-3">
            <XCircle className="text-red-500" size={24} />
            <div>
              <p className="font-medium" style={{ color: theme.text.primary }}>{t('errors.somethingWentWrong')}</p>
              <p className="text-sm" style={{ color: theme.text.muted }}>{error}</p>
            </div>
          </div>
          <Button onClick={() => fetchDashboard()} className="mt-4" variant="outline">
            {t('common.retry')}
          </Button>
        </GlassCard>
      </div>
    );
  }

  // No case state
  if (!dashboard?.has_case) {
    return (
      <div className="min-h-screen relative" style={{ background: theme.background.base }}>
        <AnimatedOvalBackground isDark={theme.isDark} />
        <div className="relative z-10 p-6 space-y-6">
          {!isInSupportMode && (
            <WelcomeProgressCard user={user} theme={theme} tasksComplete={1} totalTasks={10} t={t} />
          )}
          {isInSupportMode && effectiveClientName && (
            <GlassCard theme={theme}>
              <p className="text-sm font-medium" style={{ color: theme.text.muted }}>
                {effectiveClientName}'s Success Checklist
              </p>
              <p className="text-sm mt-1" style={{ color: theme.text.secondary }}>
                Support view, read-only
              </p>
              <div className="mt-3 flex items-center justify-between">
                <span className="text-sm" style={{ color: theme.text.primary }}>
                  {checklistProgress.completed} of {checklistProgress.total} steps complete
                </span>
                <Button variant="outline" onClick={() => setShowChecklist(true)}>
                  View checklist
                </Button>
              </div>
            </GlassCard>
          )}
          <GlassCard theme={theme} className="text-center py-12">
            <div 
              className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
              style={{ background: `${theme.accent}20` }}
            >
              <FileText size={32} style={{ color: theme.accent }} />
            </div>
            <h2 className="text-xl font-semibold mb-2" style={{ color: theme.text.primary }}>
              No credit report available. Please import a report.
            </h2>
            <p className="max-w-md mx-auto mb-6" style={{ color: theme.text.secondary }}>
              {t('dashboard.importToGetStarted')}
            </p>
            <Link to="/credit-report/import">
              <Button className="text-white" style={{ background: theme.accent }}>
                <Upload size={18} className="mr-2" />
                {t('dashboard.importCreditReport')}
              </Button>
            </Link>
          </GlassCard>
        </div>
      </div>
    );
  }

  const { scores, tradelines, dispute_workflow, report, recent_activity, credit_builder, disputes_summary } = dashboard;

  // Get unified data for consistent display
  const bureauScoresData = unifiedSummary?.bureau_scores || {};
  const creditReportCardData = unifiedSummary?.credit_report_card || {};
  const creditBuilderCardData = unifiedSummary?.credit_builder_card || {};
  const summaryKpisData = unifiedSummary?.summary_kpis || {};
  const dashboardRealCounts = unifiedSummary?.dashboard_real_counts_debug || null;
  const hasUnifiedReportData = unifiedSummary?.has_data === true;

  const importProvider = (report?.import_provider || creditReportCardData.provider || '')
    .toLowerCase()
    .replace(/\s/g, '')
    .replace(/-/g, '')
    .replace(/_/g, '');
  const isCredentialImport = report?.import_source === 'credential_import'
    || report?.origin?.method === 'credential_import';
  const hasSavedReport = Boolean(report?.report_id || selectedReportId || report?.has_report);

  const resolveDashboardKpiValue = (realCountKey, summaryKey, fallbackWhenMissing = '—') => {
    if (dashboardRealCounts && dashboardRealCounts[realCountKey] != null) {
      return Number(dashboardRealCounts[realCountKey]);
    }
    const summaryValue = summaryKpisData[summaryKey]?.value;
    if (summaryKpisData[summaryKey]?.computed === true && summaryValue != null) {
      return Number(summaryValue);
    }
    if (hasUnifiedReportData || report?.has_report) {
      return fallbackWhenMissing;
    }
    return 0;
  };

  const strictInitialNegative = resolveDashboardKpiValue(
    'strict_negative_tradelines',
    'initial_negative'
  );
  const strictRemainingItems = resolveDashboardKpiValue(
    'total_disputable_items',
    'remaining_items'
  );
  
  // Use unified scores if available, otherwise fallback to dashboard scores
  const getScore = (bureau) => {
    const bureauLower = bureau.toLowerCase();
    const raw =
      bureauScoresData[bureauLower]?.score !== undefined
        ? bureauScoresData[bureauLower].score
        : scores?.[bureauLower];
    if (raw == null || raw === 0 || raw === 300) return null;
    return raw;
  };
  
  // Get score change data from unified summary
  const getScoreChange = (bureau) => {
    const bureauLower = bureau.toLowerCase();
    return bureauScoresData[bureauLower]?.score_change || null;
  };
  
  // Get last updated date from unified summary
  const getLastUpdated = (bureau) => {
    const bureauLower = bureau.toLowerCase();
    if (bureauScoresData[bureauLower]?.last_updated) {
      return bureauScoresData[bureauLower].last_updated;
    }
    return report?.imported_at;
  };

  return (
    <div className="relative" data-testid="selfserve-dashboard">
      {/* Animated Background */}
      <AnimatedOvalBackground isDark={theme.isDark} />
      
      {/* Content */}
      <div className="relative z-10 space-y-4 sm:space-y-6 px-3 sm:px-4 lg:px-0 max-w-full min-w-0 overflow-x-hidden pb-6 sm:pb-8">
        {/* Dashboard Welcome Card */}
        {showWelcomeCard && !isViewingAsSupport && (
          <DashboardWelcomeCard onDismiss={handleDismissWelcome} />
        )}

        {/* Trial Status Banner - Shows days remaining and upgrade CTA */}
        <TrialStatusBanner />

        {/* SECTION 1: Checklist card */}
        {!isInSupportMode && (
          <WelcomeProgressCard 
            user={user} 
            theme={theme}
            tasksComplete={checklistProgress.completed}
            totalTasks={checklistProgress.total}
            onClick={() => setShowChecklist(true)}
            t={t}
          />
        )}

        {isInSupportMode && effectiveClientName && (
          <GlassCard theme={theme}>
            <p className="text-sm font-medium" style={{ color: theme.text.muted }}>
              {effectiveClientName}'s Success Checklist
            </p>
            <p className="text-sm mt-1" style={{ color: theme.text.secondary }}>
              Support view, read-only
            </p>
            <div className="mt-3 flex items-center justify-between">
              <span className="text-sm" style={{ color: theme.text.primary }}>
                {checklistProgress.completed} of {checklistProgress.total} steps complete
              </span>
              <Button variant="outline" onClick={() => setShowChecklist(true)}>
                View checklist
              </Button>
            </div>
            <p className="text-xs mt-2" style={{ color: theme.text.muted }}>
              Loaded from effective support context.
            </p>
          </GlassCard>
        )}

        {/* SECTION 1.5: Report History Selector */}
        <section 
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
          data-testid="dashboard-report-history-section"
        >
          <div className="flex flex-wrap items-center gap-2 sm:gap-4">
            <ReportHistorySelector 
              onReportChange={handleReportChange}
            />
            {isViewingHistorical && (
              <span 
                className="px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-[10px] sm:text-xs font-bold whitespace-nowrap"
                style={{
                  background: theme.isDark ? 'rgba(234,178,58,0.14)' : 'rgba(234,178,58,0.12)',
                  color: theme.isDark ? '#F0C160' : '#CA8A04',
                }}
                data-testid="historical-report-indicator"
              >
                Viewing Historical Report
              </span>
            )}
          </div>
        </section>

        {/* SECTION 2: Credit Score Hero */}
        <div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <h2 className="text-lg font-semibold" style={{ color: theme.text.primary }}>
              {t('dashboard.creditScores')}
            </h2>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              {isCredentialImport && importProvider && (
                <>
                  <Link to="/credit-report">
                    <button
                      type="button"
                      className="inline-flex items-center justify-center px-4 py-2 rounded-xl font-semibold text-sm text-white transition-all duration-200"
                      style={{
                        background: theme.accent,
                        boxShadow: theme.isDark
                          ? '0 8px 24px rgba(0,0,0,0.25)'
                          : '0 8px 20px rgba(37, 99, 235, 0.25)',
                      }}
                      data-testid="view-latest-report-btn"
                    >
                      {t('creditReport.viewLatestReport')}
                    </button>
                  </Link>
                  <ProviderRefreshActionButton
                    provider={importProvider}
                    lastUpdated={report?.imported_at}
                    hasSavedReport={hasSavedReport}
                    theme={theme}
                    onImportStarted={(importId) => {
                      setRefreshImportId(importId);
                      setRefreshImportSession({
                        importId,
                        startedAt: new Date().toISOString(),
                      });
                    }}
                    onViewReport={handleViewCreditReport}
                    onNeedCredentials={() => setShowReimportModal(true)}
                    onRefreshMetadataUpdated={() => fetchDashboard(selectedReportId, { force: true })}
                    data-testid="dashboard-refresh-report-btn"
                  />
                </>
              )}
              {/* Share Snapshot Button - Premium Glassmorphism Style */}
              <button
                onClick={() => setShowSnapshotModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-semibold text-sm transition-all duration-200"
                style={{
                  background: theme.isDark 
                    ? 'rgba(167, 243, 208, 0.12)' 
                    : 'rgba(255, 255, 255, 0.65)',
                  border: `1px solid ${theme.isDark ? 'rgba(167, 243, 208, 0.25)' : 'rgba(16, 185, 129, 0.28)'}`,
                  color: theme.isDark ? '#A7F3D0' : '#047857',
                  backdropFilter: 'blur(14px)',
                  WebkitBackdropFilter: 'blur(14px)',
                  boxShadow: theme.isDark
                    ? '0 8px 24px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                    : '0 10px 30px rgba(16, 185, 129, 0.10), inset 0 1px 0 rgba(255, 255, 255, 0.65)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = theme.isDark 
                    ? 'rgba(167, 243, 208, 0.18)' 
                    : 'rgba(236, 253, 245, 0.75)';
                  e.currentTarget.style.borderColor = theme.isDark 
                    ? 'rgba(167, 243, 208, 0.4)' 
                    : 'rgba(16, 185, 129, 0.45)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = theme.isDark
                    ? '0 12px 32px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.15)'
                    : '0 14px 34px rgba(16, 185, 129, 0.16), inset 0 1px 0 rgba(255, 255, 255, 0.78)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = theme.isDark 
                    ? 'rgba(167, 243, 208, 0.12)' 
                    : 'rgba(255, 255, 255, 0.65)';
                  e.currentTarget.style.borderColor = theme.isDark 
                    ? 'rgba(167, 243, 208, 0.25)' 
                    : 'rgba(16, 185, 129, 0.28)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = theme.isDark
                    ? '0 8px 24px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                    : '0 10px 30px rgba(16, 185, 129, 0.10), inset 0 1px 0 rgba(255, 255, 255, 0.65)';
                }}
                data-testid="share-snapshot-btn"
              >
                <Camera size={16} />
                <span className="hidden sm:inline">Share Snapshot</span>
                <span className="sm:hidden">Share</span>
              </button>
              <Link 
                to="/credit-report" 
                className="text-xs sm:text-sm font-medium flex items-center gap-1 whitespace-nowrap"
                style={{ color: theme.accent }}
              >
                <span className="hidden sm:inline">{t('common.viewFullReport')}</span>
                <span className="sm:hidden">View Report</span>
                <ArrowRight size={14} />
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
            {['Equifax', 'Experian', 'TransUnion'].map((bureau) => (
              <BureauScoreCard
                key={bureau}
                bureau={bureau}
                score={getScore(bureau)}
                baselineScore={dashboard?.previous_scores?.[bureau.toLowerCase()] || null}
                initialScore={dashboard?.initial_scores?.[bureau.toLowerCase()] || null}
                lastUpdated={getLastUpdated(bureau)}
                unifiedScoreChange={getScoreChange(bureau)}
                theme={theme}
                animate={animateScores}
                t={t}
              />
            ))}
          </div>
        </div>

        {/* SECTION 2.5: KPI Row - Status Metrics (Moved up for marketing screenshot optimization) */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
          <KPICard 
            icon={AlertCircle} 
            label={t('dashboard.initialNegative')}
            value={strictInitialNegative}
            color="#EF4444"
            theme={theme}
          />
          <KPICard 
            icon={CheckCircle2} 
            label={t('dashboard.initialPositive')}
            value={
              summaryKpisData.initial_positive?.computed === true 
                ? summaryKpisData.initial_positive?.value 
                : (tradelines?.initial_positive ?? tradelines?.positive ?? '—')
            }
            color="#EAB23A"
            theme={theme}
          />
          <KPICard 
            icon={Shield} 
            label={t('dashboard.verifiedUpdated')}
            value={
              summaryKpisData.verified_updated?.computed === true 
                ? summaryKpisData.verified_updated?.value 
                : (tradelines?.verified_updated ?? dispute_workflow?.verified ?? '—')
            }
            color="#EAB23A"
            theme={theme}
          />
          <KPICard 
            icon={Check} 
            label={t('dashboard.removedRepaired')}
            value={
              summaryKpisData.removed_repaired?.computed === true 
                ? summaryKpisData.removed_repaired?.value 
                : (tradelines?.removed_repaired ?? dispute_workflow?.repaired ?? '—')
            }
            color="#EAB23A"
            theme={theme}
          />
          <KPICard 
            icon={Plus} 
            label={t('dashboard.recentlyAdded')}
            value={
              summaryKpisData.recently_added?.computed === true 
                ? summaryKpisData.recently_added?.value 
                : (summaryKpisData.recently_added?.reason === 'NO_COMPARISON_REPORT' 
                    ? '—' 
                    : (tradelines?.recently_added ?? '—'))
            }
            color="#EAB23A"
            theme={theme}
          />
          <KPICard 
            icon={AlertTriangle} 
            label={t('dashboard.remainingItems')}
            value={strictRemainingItems}
            color="#EAB23A"
            theme={theme}
          />
        </div>

        {/* SECTION 3: Credit Profile Snapshot */}
        <CreditProfileSnapshot theme={theme} reportId={selectedReportId} />

        {/* SECTION 4: Module Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          <ModuleSummaryCard
            title={t('nav.creditReport')}
            icon={FileText}
            to="/credit-report"
            accentColor="#0FAC6C"
            theme={theme}
            items={[
              { label: t('dashboard.lastImported'), value: creditReportCardData.last_imported || (report?.imported_at ? new Date(report.imported_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : t('dashboard.notImported')) },
              { label: t('dashboard.provider') || 'Provider', value: creditReportCardData.provider || report?.import_provider || report?.provider || t('dashboard.notAvailable') },
              { label: t('dashboard.bureausImported'), value: creditReportCardData.bureaus_imported ?? report?.bureaus_count ?? Object.keys(scores || {}).length },
              { label: t('dashboard.accountsFound'), value: creditReportCardData.accounts_found ?? tradelines?.total ?? 0 },
            ]}
          />
          <ModuleSummaryCard
            title={t('nav.myDisputes')}
            icon={Gavel}
            to="/disputes"
            accentColor="#EAB23A"
            theme={theme}
            items={[
              { label: t('dashboard.activeRounds'), value: dashboard?.disputes_summary?.active_rounds ?? dispute_workflow?.active_disputes ?? 0, highlight: dashboard?.disputes_summary?.active_rounds > 0 },
              { label: t('dashboard.lettersReady'), value: dashboard?.disputes_summary?.letters_ready ?? dispute_workflow?.candidates_selected ?? 0 },
              { label: t('dashboard.sent'), value: dashboard?.disputes_summary?.sent ?? dispute_workflow?.candidates_disputed ?? 0 },
              { label: t('dashboard.awaitingResponse'), value: dashboard?.disputes_summary?.awaiting_response ?? 0 },
              { label: t('dashboard.closed'), value: dashboard?.disputes_summary?.closed ?? 0 },
            ]}
          />
          <ModuleSummaryCard
            title={t('nav.creditBuilder')}
            icon={TrendingUp}
            to="/credit-builder/utilization"
            accentColor="#0D9F64"
            theme={theme}
            items={[
              { label: t('dashboard.utilization'), value: creditBuilderCardData.utilization_label || (dashboard?.credit_builder?.utilization != null ? `${dashboard.credit_builder.utilization}%` : t('dashboard.notAvailable')), highlight: creditBuilderCardData.utilization != null || dashboard?.credit_builder?.utilization != null },
              { label: t('dashboard.paymentPlan'), value: creditBuilderCardData.payment_plan || dashboard?.credit_builder?.payment_plan || t('dashboard.notSet') },
              { label: t('dashboard.scoreTarget'), value: creditBuilderCardData.score_target || dashboard?.credit_builder?.score_target || t('dashboard.notSet') },
              { label: t('dashboard.tradelineRecs'), value: creditBuilderCardData.tradeline_recs ?? dashboard?.credit_builder?.tradeline_recs ?? 0 },
            ]}
          />
        </div>

        {/* SECTION 5: AI Insights Carousel */}
        <AIInsightsCarousel
          theme={theme}
          t={t}
          strictNegativeCount={strictInitialNegative}
          strictRemainingCount={strictRemainingItems}
          scores={scores}
          dispute_workflow={dispute_workflow}
        />

        {/* SECTION 6: Lower Section - History & Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 min-w-0">
          <ScoreBreakdown
            selectedReportId={selectedReportId || dashboard?.report?.report_id}
            onReportChange={handleReportChange}
            effectiveUserId={effectiveUserId}
            theme={theme}
            cardWrapper={GlassCard}
          />
          <RecentActivityPanel activity={recent_activity} theme={theme} t={t} />
        </div>

        {/* Refresh Button */}
        <div className="flex justify-center pt-4">
          <Button 
            variant="outline" 
            onClick={() => fetchDashboard()}
            className="gap-2"
            style={{ 
              borderColor: theme.card.border,
              color: theme.text.secondary,
              background: theme.card.fill
            }}
          >
            <RefreshCw size={16} />
            {t('dashboard.refreshDashboard')}
          </Button>
        </div>
      </div>

      {/* Success Checklist Modal */}
      <SuccessChecklistModal
        isOpen={showChecklist}
        onClose={() => setShowChecklist(false)}
        theme={theme}
        navigate={navigate}
        t={t}
        isSupportMode={isInSupportMode}
      />

      {/* Share Snapshot Modal */}
      <ShareSnapshotModal
        isOpen={showSnapshotModal}
        onClose={() => setShowSnapshotModal(false)}
        userData={user}
        scores={{
          equifax: getScore('Equifax'),
          experian: getScore('Experian'),
          transunion: getScore('TransUnion'),
          equifaxFirstReport: getScoreChange('Equifax')?.direction === 'baseline',
          experianFirstReport: getScoreChange('Experian')?.direction === 'baseline',
          transunionFirstReport: getScoreChange('TransUnion')?.direction === 'baseline',
        }}
        metrics={{
          initialNegative: strictInitialNegative,
          initialPositive: summaryKpisData.initial_positive?.computed 
            ? summaryKpisData.initial_positive?.value 
            : (tradelines?.initial_positive ?? tradelines?.positive ?? '—'),
          verifiedUpdated: summaryKpisData.verified_updated?.computed 
            ? summaryKpisData.verified_updated?.value 
            : (tradelines?.verified_updated ?? dispute_workflow?.verified ?? '—'),
          removedRepaired: summaryKpisData.removed_repaired?.computed 
            ? summaryKpisData.removed_repaired?.value 
            : (tradelines?.removed_repaired ?? dispute_workflow?.repaired ?? '—'),
          recentlyAdded: summaryKpisData.recently_added?.computed 
            ? summaryKpisData.recently_added?.value 
            : (tradelines?.recently_added ?? '—'),
          remainingItems: strictRemainingItems,
        }}
        lastUpdated={report?.imported_at}
        theme={theme}
      />

      {isCredentialImport && importProvider && (
        <>
          <ReimportModal
            isOpen={showReimportModal}
            onClose={() => setShowReimportModal(false)}
            report={report}
            theme={theme}
            onImportComplete={() => fetchDashboard(selectedReportId, { force: true })}
          />
          <ProviderRefreshImportDialog
            isOpen={Boolean(refreshImportId)}
            importId={refreshImportId}
            importSession={refreshImportSession}
            getAuthHeaders={getAuthHeaders}
            onComplete={handleRefreshImportComplete}
            onError={handleRefreshImportError}
            onClose={() => {
              setRefreshImportId(null);
              setRefreshImportSession(null);
            }}
          />
        </>
      )}
    </div>
  );
};

export default SelfServeDashboard;
